import type { OrganizationForCarRecord, TireSeason } from '@navio/nsdk';
import { BookingStoreCar } from 'noddi-types';
import type { StationarySlotHold } from 'noddi-web';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { SelectedOrganization, StationaryGarage, StationaryService } from '../types/stationaryBooking';

interface StationaryBookingState {
  clearStore: () => void;
  serviceCenter: StationaryGarage | null;
  setServiceCenter: (serviceCenter: StationaryGarage) => void;
  /**
   * Change location after the car is already chosen (e.g. tire hotel at another
   * department). Keeps the car; still clears services and any held slot.
   */
  switchServiceCenter: (serviceCenter: StationaryGarage) => void;
  primaryService: StationaryService | null;
  setPrimaryService: (service: StationaryService | null) => void;
  addonServices: StationaryService[];
  setAddonServices: (services: StationaryService[]) => void;
  removeAddonService: (salesItemId: number) => void;
  addAddonService: (service: StationaryService) => void;
  /** The wheel set that goes into storage on a new tire hotel signup; null until the customer chooses. */
  incomingTireSeason: TireSeason | null;
  setIncomingTireSeason: (season: TireSeason) => void;
  /** Held slot + credentials (shared {@link StationarySlotHold}; never split). */
  reservation: StationarySlotHold | null;
  setReservation: (reservation: StationarySlotHold | null) => void;
  car: BookingStoreCar | null;
  setCar: (car: BookingStoreCar) => void;
  removeCar: () => void;
  selectedOrganization: SelectedOrganization | null;
  setSelectedOrganization: (organization: OrganizationForCarRecord | null) => void;
  /** "Continue here" on the tire hotel relocation prompt — holds until car/location changes. */
  tireHotelPromptDismissed: boolean;
  dismissTireHotelPrompt: () => void;
}

const initialState = {
  serviceCenter: null,
  primaryService: null,
  addonServices: [] as StationaryService[],
  incomingTireSeason: null,
  reservation: null,
  car: null,
  selectedOrganization: null,
  tireHotelPromptDismissed: false
};

// Wiped whenever the held slot is invalidated (time re-pick, addon change, etc.).
const reservationReset = {
  reservation: null as StationarySlotHold | null
};

// Wiped when an upstream choice (location/car) changes and the whole draft below it is stale.
const selectionReset = {
  primaryService: null as StationaryService | null,
  addonServices: [] as StationaryService[],
  incomingTireSeason: null as TireSeason | null,
  tireHotelPromptDismissed: false,
  ...reservationReset
};

export const useStationaryBookingStore = create<StationaryBookingState>()(
  persist(
    devtools((set) => ({
      ...initialState,
      clearStore: () => set(initialState),

      setServiceCenter: (serviceCenter) =>
        set({
          serviceCenter,
          // Changing location invalidates the rest of the booking draft
          car: null,
          selectedOrganization: null,
          ...selectionReset
        }),
      switchServiceCenter: (serviceCenter) =>
        set({
          serviceCenter,
          ...selectionReset
        }),
      setPrimaryService: (service) =>
        set((state) =>
          // Changing the primary service invalidates any slot held for the old service set,
          // and its addons: they are offered per primary, so they'd be hidden yet still booked.
          // ExtraServiceCards re-adds the new primary's locked addons.
          state.primaryService?.salesItemId === service?.salesItemId
            ? { primaryService: service }
            : { primaryService: service, addonServices: [], incomingTireSeason: null, ...reservationReset }
        ),
      setAddonServices: (services) => set({ addonServices: services, ...reservationReset }),
      removeAddonService: (salesItemId) =>
        set((state) => ({
          addonServices: state.addonServices.filter((service) => service.salesItemId !== salesItemId),
          ...reservationReset
        })),
      addAddonService: (service) =>
        set((state) => ({
          addonServices: [...state.addonServices, service],
          ...reservationReset
        })),
      setIncomingTireSeason: (incomingTireSeason) => set({ incomingTireSeason }),
      setReservation: (reservation) => set({ reservation }),
      dismissTireHotelPrompt: () => set({ tireHotelPromptDismissed: true }),
      setCar: (car) => set({ car, ...selectionReset }),
      removeCar: () => set({ car: null, ...selectionReset }),
      setSelectedOrganization: (organization) =>
        set({
          // Persist only the scalar choice, never the nested API record (carManagers etc.).
          selectedOrganization: organization
            ? {
                organizationId: organization.organizationId,
                organizationName: organization.organizationName,
                registrationNumber: organization.registrationNumber,
                userGroupId: organization.userGroupId
              }
            : null
        })
    })),
    {
      // v4: reservation is flat StationarySlotHold (was nested StationaryReservation).
      name: 'stationary-booking-store-v4',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

export const useServiceCenter = () => useStationaryBookingStore((state) => state.serviceCenter);
export const useStationaryPrimaryService = () => useStationaryBookingStore((state) => state.primaryService);
export const useStationaryAddonServices = () => useStationaryBookingStore((state) => state.addonServices);
export const useStationaryIncomingTireSeason = () => useStationaryBookingStore((state) => state.incomingTireSeason);
export const useStationaryReservation = () => useStationaryBookingStore((state) => state.reservation);
export const useStationaryBookingCar = () => useStationaryBookingStore((state) => state.car);
export const useStationaryBookingSelectedOrganization = () =>
  useStationaryBookingStore((state) => state.selectedOrganization);
export const useTireHotelPromptDismissed = () => useStationaryBookingStore((state) => state.tireHotelPromptDismissed);

export const useStationaryBookingActions = () => {
  const setServiceCenter = useStationaryBookingStore((state) => state.setServiceCenter);
  const switchServiceCenter = useStationaryBookingStore((state) => state.switchServiceCenter);
  const setPrimaryService = useStationaryBookingStore((state) => state.setPrimaryService);
  const setAddonServices = useStationaryBookingStore((state) => state.setAddonServices);
  const removeAddonService = useStationaryBookingStore((state) => state.removeAddonService);
  const addAddonService = useStationaryBookingStore((state) => state.addAddonService);
  const setIncomingTireSeason = useStationaryBookingStore((state) => state.setIncomingTireSeason);
  const setReservation = useStationaryBookingStore((state) => state.setReservation);
  const clearStore = useStationaryBookingStore((state) => state.clearStore);
  const setCar = useStationaryBookingStore((state) => state.setCar);
  const removeCar = useStationaryBookingStore((state) => state.removeCar);
  const setSelectedOrganization = useStationaryBookingStore((state) => state.setSelectedOrganization);
  const dismissTireHotelPrompt = useStationaryBookingStore((state) => state.dismissTireHotelPrompt);
  return {
    setServiceCenter,
    switchServiceCenter,
    setPrimaryService,
    setAddonServices,
    removeAddonService,
    addAddonService,
    setIncomingTireSeason,
    setReservation,
    clearStore,
    setCar,
    removeCar,
    setSelectedOrganization,
    dismissTireHotelPrompt
  };
};
